import { useMeta } from '@/stores/meta.js'
import { useUi } from '@/stores/ui.js'
import { getRecord, saveRecord } from './records.js'
import { toInputs } from './forms.js'
import { fieldsInErrors, missingFields } from './requiredFields.js'
import { t } from '@/i18n/index.js'

/**
 * A write outside the record's form (a stage dragged, a record linked, a phone added…) that asks
 * for the fields the record must have and does not (D-343): before sending — the company's rules
 * known in the browser, offline too — and again when Core refuses it for such fields. Cancelled,
 * nothing is written: `{status: 'cancelled'}`.
 *
 * `record`: the record as stored, when the caller has it (else read; never a list's row — it lacks
 * fields); `body`: the write; `shown`: fields the caller's own form shows (not asked again).
 */
export async function saveFilling(entity, uuid, body, options = {}) {
  const meta = useMeta()
  const ui = useUi()
  let record = options.record ?? null
  if (!record && uuid) record = (await getRecord(entity, uuid).catch(() => null))?.data || null
  const inputs = () => {
    const base = toInputs(entity, record, meta).inputs
    return { ...base, ...body, custom_fields: { ...(base.custom_fields || {}), ...(body.custom_fields || {}) } }
  }
  const ask = async (names, errors = {}) => {
    const patch = await ui.askFields({ entity, record, names, inputs: inputs(), errors, message: options.message || t('missingFields.message') })
    if (!patch) return false
    body = { ...body, ...patch, ...(patch.custom_fields ? { custom_fields: { ...(body.custom_fields || {}), ...patch.custom_fields } } : {}) }
    return true
  }
  const before = missingFields(entity, inputs(), meta, { original: record, shown: options.shown || [] })
  if (before.length && !(await ask(before))) return { status: 'cancelled' }
  for (;;) {
    const result = await saveRecord(entity, uuid, body, { baseVersion: options.baseVersion ?? record?.version ?? null, label: options.label ?? record?.label })
    const names = result.status === 'invalid' ? fieldsInErrors(entity, result.errors, meta) : []
    if (!names.length) return result
    if (!(await ask(names, result.errors))) return { status: 'cancelled' }
  }
}

/**
 * An action Core does on a record (a suggestion taken, an AI answer applied, records merged) that
 * Core refused for fields the record must have (D-343): asked for them, saved to the record, and
 * the action tried again. `run` throws the API's error; the answer is its result, or null when
 * cancelled.
 */
export async function retryFilling(entity, uuid, run) {
  const meta = useMeta()
  for (;;) {
    try {
      return await run()
    } catch (e) {
      const names = e?.code === 'FIELD_RULES_UNMET' || e?.status === 422 ? fieldsInErrors(entity, e.errors, meta) : []
      if (!names.length) throw e
      const saved = await saveFilling(entity, uuid, {}, { message: t('missingFields.message') })
      if (saved.status === 'cancelled') return null
      if (saved.status !== 'saved') throw e
    }
  }
}

/**
 * A queued change the server refused for fields the record must have (D-343), corrected on the
 * sync screen (addictive-offline's PendingOperations `fix`): asked for the fields it named, and
 * the change's body with them — or null (cancelled, or nothing here can correct it: a change
 * naming fields of another record, as a qualification's).
 */
export async function fixRefused(operation) {
  const meta = useMeta()
  const ui = useUi()
  const entity = operation.entity
  const names = entity ? fieldsInErrors(entity, operation.errors, meta) : []
  if (!names.length) {
    ui.warn(t('missingFields.cannotFix'))
    return null
  }
  const record = operation.uuid ? (await getRecord(entity, operation.uuid).catch(() => null))?.data || null : null
  const body = operation.body || {}
  const base = toInputs(entity, record, meta).inputs
  const patch = await ui.askFields({ entity, record, names, errors: operation.errors, message: t('missingFields.queued'),
    inputs: { ...base, ...body, custom_fields: { ...(base.custom_fields || {}), ...(body.custom_fields || {}) } } })
  if (!patch) return null
  return { ...body, ...patch, ...(patch.custom_fields ? { custom_fields: { ...(body.custom_fields || {}), ...patch.custom_fields } } : {}) }
}
